import {PBEnhancedContentCard, SimpleBrazeContentCard, contentCardIds, hideIf} from "../ContentCard"
import {BrazeLogger} from "../../services/brazeLogger"
import {Context} from "../context"
import {filterBanners} from "./components/banner"

/**
 * Braze Adapter
 * converts the Braze structure to our internal context shape
 * This allows all clients to consume the same notifications in a library agnostic way
 */
export function brazeAdapter(brazeCards: SimpleBrazeContentCard[], existingContentcards: Context, logger: BrazeLogger): Context {
  const sortedCards = sortCardsByPinned(...brazeCards.filter((c) => !c.dismissed))

  const notificationCards = sortedCards.filter((c) => !c.extras?.component_id)

  const contentCards = sortedCards
    .filter((c) => c.extras?.component_id)
    .map(parseFields(logger))
    .filter(shouldHide)
    .sort((a, b) => b.extras.priority - a.extras.priority || sortByCreated(a, b))

  const data = {
    notifications: notificationCards,
    notificationsCount: notificationCards.filter((notification) => !notification.viewed).length,
    banners: filterBanners(contentCards),
    contentCardPopup: existingContentcards.contentCardPopup
  }

  logger.log({originalBrazeEvents: brazeCards, ...data})

  return data
}

/**
 * Braze's supports an `extras` field, which is a `Record<string,string>`, so to
 * support advanced customisation, certain known keys are provided as stringified JSON
 * we also map the card.id into the extras, and component fields, as this is easier to consume
 * in the downstream components
 */
function parseFields(logger: BrazeLogger) {
  const tryJson = tryJsonAndLog(logger)
  return (card: SimpleBrazeContentCard): PBEnhancedContentCard => {
    const {extras} = card
    return {
      ...card,
      extras: {
        ...extras,
        id: card.id,
        priority: extras?.priority ? parseInt(extras.priority) : 0,
        ...(extras?.showOnPages && {showOnPages: tryJson(extras.showOnPages)}),
        ...(extras?.banner && {banner: {...tryJson(extras.banner), id: card.id}}),
        ...(extras?.popup && {popup: {...tryJson(extras.popup), id: card.id}})
      }
    }
  }
}

export function tryJsonAndLog(logger: BrazeLogger) {
  return (v: any): any => {
    if (typeof v !== "string") {
      return v
    }
    // replace all escape characters from stringified json
    // if these aren't escaped the JSON.parse will blow up
    // there may be more characters that cause this
    // I couldn't figure out how to do a more clever regex, e.g /([\r\t\r])/ doesn't seem to work
    const escapedString = v.replaceAll(/\n/g, "\\n").replaceAll(/\r/g, "\\r").replaceAll(/\t/g, "\\t")

    try {
      return JSON.parse(escapedString)
    } catch (e: any) {
      // here we attempt to pull out the position of the failing character in the JSON
      const [pos] = (e.message as string).match(/\d+/) ?? []
      const parsedPos = parseInt(pos ?? "nan")
      const posMsg = !Number.isNaN(parsedPos) ? `\nLikely char: "${escapedString.at(parsedPos)}"` : ""

      logger.warn(`JSON parse error: ${e.message}\noriginal "${v}"\rreplaced: "${escapedString}"\n${posMsg}`)
      return undefined
    }
  }
}

function sortCardsByPinned(...cards: SimpleBrazeContentCard[]): SimpleBrazeContentCard[] {
  const pinned = cards.filter((c) => c.pinned).sort(sortByCreated)
  const unpinned = cards.filter((c) => !c.pinned).sort(sortByCreated)
  return pinned.concat(unpinned)
}

function sortByCreated({created: a}: SimpleBrazeContentCard, {created: b}: SimpleBrazeContentCard): number {
  return (a ?? new Date(0)) < (b ?? new Date(0)) ? 1 : -1
}

function shouldHide(c: PBEnhancedContentCard) {
  return !hideIf.find((state) => contentCardIds.find((id) => c.extras[id]?.hideIf === state && c[state] === true))
}
